import { clsx } from '@alexis/helpers/clsx';
import { translateText } from '@alexis/helpers/translation';
import React from 'react';
import { useSelector } from 'react-redux';

import styles from '@components/flights/TripDetail/Segment.module.scss';

import { CLOUDINARY_DAILY_KEY } from '@constants/cloudinary';

import { cloudinaryImageTransformations } from '@helpers/imageTransformations';

import ArrowAngleIcon from '@icons/arrow_angle.svg';
import LayoutGridIcon from '@icons/layout_grid.svg';
import PassengerSeatIcon from '@icons/passenger_seat.svg';

import { getIsRtlState, getLocaleState, getTranslationsState } from '@redux/selectors';

import type { FlightFareResultTripLeg } from '@wegoTypes/flights/metasearch/flightFareResultTripLeg';
import type { FlightFareResultTripLegSegment } from '@wegoTypes/flights/metasearch/flightFareResultTripLegSegment';
import type { Translations } from '@wegoTypes/translations';

interface SegmentProps {
  flightAmenities: FlightAmenities | undefined;
  leg: FlightFareResultTripLeg;
  legIndex: number;
  segment: FlightFareResultTripLegSegment;
  segmentIndex: number;
}

const Segment: React.FC<SegmentProps> = ({
  flightAmenities,
  leg,
  legIndex,
  segment,
  segmentIndex,
}) => {
  const isRtl = useSelector(getIsRtlState);
  const locale = useSelector(getLocaleState);
  const translations = useSelector(getTranslationsState);

  const stopoverIndicator = segment.stopoverIndicators ? segment.stopoverIndicators[0] : undefined;

  const segmentFlightAmenities = flightAmenities
    ? flightAmenities.legs[legIndex]?.segments[segmentIndex]
    : null;

  const freshFoodAmenity =
    !!segmentFlightAmenities && !!segmentFlightAmenities.amenity
      ? flightAmenities!.freshFoods.find(
          (freshFood) => freshFood.id === segmentFlightAmenities.amenity!.freshFoodId,
        )
      : undefined;

  const entertainmentAmenity =
    !!segmentFlightAmenities && !!segmentFlightAmenities.amenity
      ? flightAmenities!.entertainments.find(
          (entertainment) => entertainment.id === segmentFlightAmenities.amenity!.entertainmentId,
        )
      : undefined;

  const wifiAmenity =
    !!segmentFlightAmenities && !!segmentFlightAmenities.amenity
      ? flightAmenities!.wifis.find((wifi) => wifi.id === segmentFlightAmenities.amenity!.wifiId)
      : undefined;

  const powerAmenity =
    !!segmentFlightAmenities && !!segmentFlightAmenities.amenity
      ? flightAmenities!.powers.find(
          (power) => power.id === segmentFlightAmenities.amenity!.powerId,
        )
      : undefined;

  const rowLayout =
    !!segmentFlightAmenities && !!segmentFlightAmenities.amenity
      ? flightAmenities!.layouts.find(
          (layout) => layout.id === segmentFlightAmenities.amenity!.layoutId,
        )
      : undefined;

  const seat =
    !!segmentFlightAmenities && !!segmentFlightAmenities.amenity
      ? flightAmenities!.seats.find((seat) => seat.id === segmentFlightAmenities.amenity!.seatId)
      : undefined;

  const getStopoverIndicatorMessageDescription = (
    stopoverIndicator: string,
    translations: Translations,
  ): string => {
    switch (stopoverIndicator) {
      case 'technicalStopover':
        return translations.technical_stopover_desc as string;
      case 'changeAirport':
        return translations.change_terminal_airport_desc as string;
      case 'changeTerminal':
        return translations.change_terminal_airport_desc as string;
      default:
        return '';
    }
  };

  const getStopoverIndicatorMessage = (
    stopoverIndicator: string,
    translations: Translations,
  ): string => {
    switch (stopoverIndicator) {
      case 'technicalStopover':
        return translations.technical_stopover as string;
      case 'changeAirport':
        return translations.change_airport_in as string;
      case 'changeTerminal':
        return translations.change_terminal_in as string;
      case 'changeFlight':
        return translations.change_flight as string;
      default:
        return '';
    }
  };

  const getLegroomText = (key: string, translations: Translations) => {
    switch (key) {
      case 'standard':
        return translations.standard_legroom;

      case 'more':
        return translations.above_average_legroom;

      case 'less':
        return translations.below_average_legroom;

      default:
        return translations.info_not_available;
    }
  };

  const getLegroomAndPitchDetails = (seat: FlightAmenitiesSeat, translations: Translations) => {
    const legroomDetail = seat.legroom ? getLegroomText(seat.legroom, translations) : '';
    const pitchDetail = seat.displayText ? `(${seat.displayText})` : '';
    const separator = legroomDetail && pitchDetail ? ' ' : '';
    return `${legroomDetail}${separator}${pitchDetail}`;
  };

  return (
    <div
      data-pw='itineraryCard_segmentDetails'
      data-testid='flights-details-segment'
      key={segmentIndex}
      className={clsx(styles.segment, isRtl && styles.rtl)}
    >
      <div data-pw='segment_departureInfo' className={styles.start}>
        <div className={styles.dateTime}>
          <div
            className={clsx(
              styles.date,
              segment.departureDate !== leg.segments[0].departureDate && styles.notSameDay,
            )}
          >
            {segment.departureDate}
          </div>

          <div className={styles.time} data-testid='depart-time'>
            {segment.departureTime}
          </div>
        </div>

        <div className={clsx(styles.point, segmentIndex === 0 && styles.first)}></div>

        <div className={styles.airport}>
          <span className={styles.airportCode}>{segment.departureAirportCode}</span>

          {segment.departureAirportName}
          {segment?.departureTerminal
            ? ` - ${translations.terminal} ${segment.departureTerminal}`
            : ''}
        </div>
      </div>

      <div className={styles.middle}>
        <div data-pw='segment_flightDuration' className={styles.duration}>
          <i className={clsx(styles.sprite, styles.timer)}></i>
          {segment.duration}
        </div>

        <div className={styles.verticalLine}></div>

        <div data-pw='segment_AirlineInfo' className={styles.flightInformation}>
          <div className={styles.airline}>
            <img
              className={styles.image}
              src={cloudinaryImageTransformations(
                `https://assets.wego.com/image/upload/${CLOUDINARY_DAILY_KEY}/flights/airlines_square/${segment.airlineCode}.png`,
                'c_fit',
                'w_100',
                'h_100',
                'q_auto',
                'f_auto',
                'fl_lossy',
              )}
            />
            {segment.airlineName}{' '}
            {segment.operatingAirlineName
              ? `(${translateText(translations.operated_by, locale, segment.operatingAirlineName)})`
              : null}
          </div>
          <div className={styles.airlineInformationContainer}>
            <div className={styles.airlineInformation}>
              <div className={styles.tooltipContainer}>
                <div className={styles.aircraftType}>
                  <span>{segment.designatorCode}&nbsp;</span>

                  {segment.aircraftType ? <span>({segment.aircraftType})&lrm;</span> : null}
                </div>

                <div className={clsx(styles.tooltip, styles.bottom)}>
                  <div
                    data-pw='segment_flightSpecPopup'
                    className={styles.seatMapDetailsContainerPopup}
                  >
                    <div className={styles.seatMapDetailsContainer}>
                      <div className={styles.seatMapImage}>
                        <img
                          className={styles.seatMapImage}
                          src={cloudinaryImageTransformations(
                            `https://assets.wego.com/image/upload/${CLOUDINARY_DAILY_KEY}/web/flight_amenities/seat_layout/${
                              rowLayout?.rowLayout || 'not_available'
                            }.png`,
                            'c_fit',
                            'fl_lossy',
                            'q_auto',
                            'f_auto',
                            'w_328',
                            'h_440',
                          )}
                        />
                      </div>

                      <div className={styles.seatMapDescriptionContainer}>
                        <div className={styles.description}>
                          <LayoutGridIcon className={styles.icon} />

                          {rowLayout ? rowLayout.displayText : translations.info_not_available}
                        </div>

                        <div className={styles.description}>
                          <PassengerSeatIcon className={styles.icon} />
                          {seat?.width ? translations[seat.width] : translations.info_not_available}
                        </div>

                        <div className={styles.description}>
                          <ArrowAngleIcon className={styles.icon} />
                          {seat
                            ? getLegroomAndPitchDetails(seat, translations)
                            : translations.info_not_available}
                        </div>
                      </div>
                    </div>

                    <div className={styles.seatMapDetailsDisclaimer}>
                      {translations.lbl_seat_map_info}
                    </div>
                  </div>
                </div>
              </div>

              <div className={styles.cabin}>{segment.cabin}</div>
            </div>
          </div>

          <div className={styles.segmentAmenities}>
            <div className={styles.tooltipContainer}>
              <div className={styles.amenitiesContainer}>
                <i
                  className={clsx(
                    styles.sprite,
                    freshFoodAmenity === undefined && styles.freshFoodUnknown,
                    !!freshFoodAmenity && !freshFoodAmenity.exists && styles.freshFoodNotAvailable,
                    !!freshFoodAmenity &&
                      freshFoodAmenity.exists &&
                      freshFoodAmenity.free &&
                      styles.freshFoodFree,
                    !!freshFoodAmenity &&
                      freshFoodAmenity.exists &&
                      !freshFoodAmenity.free &&
                      styles.freshFoodWithFee,
                  )}
                ></i>

                <i
                  className={clsx(
                    styles.sprite,
                    entertainmentAmenity === undefined && styles.entertainmentUnknown,
                    !!entertainmentAmenity &&
                      !entertainmentAmenity.exists &&
                      styles.entertainmentNotAvailable,
                    !!entertainmentAmenity &&
                      entertainmentAmenity.exists &&
                      entertainmentAmenity.free &&
                      styles.entertainmentFree,
                    !!entertainmentAmenity &&
                      entertainmentAmenity.exists &&
                      !entertainmentAmenity.free &&
                      styles.entertainmentWithFee,
                  )}
                ></i>

                <i
                  className={clsx(
                    styles.sprite,
                    wifiAmenity === undefined && styles.wifiUnknown,
                    !!wifiAmenity && !wifiAmenity.exists && styles.wifiNotAvailable,
                    !!wifiAmenity && wifiAmenity.exists && wifiAmenity.free && styles.wifiFree,
                    !!wifiAmenity && wifiAmenity.exists && !wifiAmenity.free && styles.wifiWithFee,
                  )}
                ></i>

                <i
                  className={clsx(
                    styles.sprite,
                    powerAmenity === undefined && styles.powerUnknown,
                    !!powerAmenity && !powerAmenity.exists && styles.powerNotAvailable,
                    !!powerAmenity && powerAmenity.exists && powerAmenity.free && styles.powerFree,
                    !!powerAmenity &&
                      powerAmenity.exists &&
                      !powerAmenity.free &&
                      styles.powerWithFee,
                  )}
                ></i>
              </div>

              <div
                data-pw='segment_flightAmenitiesPopup'
                className={clsx(styles.tooltip, styles.bottom)}
              >
                <div className={styles.flightAmenityDescription}>
                  <i
                    className={clsx(
                      styles.sprite,
                      freshFoodAmenity === undefined && styles.freshFoodUnknown,
                      !!freshFoodAmenity &&
                        !freshFoodAmenity.exists &&
                        styles.freshFoodNotAvailable,
                      !!freshFoodAmenity &&
                        freshFoodAmenity.exists &&
                        freshFoodAmenity.free &&
                        styles.freshFoodFree,
                      !!freshFoodAmenity &&
                        freshFoodAmenity.exists &&
                        !freshFoodAmenity.free &&
                        styles.freshFoodWithFee,
                    )}
                  ></i>

                  {freshFoodAmenity ? freshFoodAmenity.displayText : translations.meals_unknown}
                </div>

                <div className={styles.flightAmenityDescription}>
                  <i
                    className={clsx(
                      styles.sprite,
                      entertainmentAmenity === undefined && styles.entertainmentUnknown,
                      !!entertainmentAmenity &&
                        !entertainmentAmenity.exists &&
                        styles.entertainmentNotAvailable,
                      !!entertainmentAmenity &&
                        entertainmentAmenity.exists &&
                        entertainmentAmenity.free &&
                        styles.entertainmentFree,
                      !!entertainmentAmenity &&
                        entertainmentAmenity.exists &&
                        !entertainmentAmenity.free &&
                        styles.entertainmentWithFee,
                    )}
                  ></i>

                  {entertainmentAmenity
                    ? entertainmentAmenity.displayText
                    : translations.entertainment_unknown}
                </div>

                <div className={styles.flightAmenityDescription}>
                  <i
                    className={clsx(
                      styles.sprite,
                      wifiAmenity === undefined && styles.wifiUnknown,
                      !!wifiAmenity && !wifiAmenity.exists && styles.wifiNotAvailable,
                      !!wifiAmenity && wifiAmenity.exists && wifiAmenity.free && styles.wifiFree,
                      !!wifiAmenity &&
                        wifiAmenity.exists &&
                        !wifiAmenity.free &&
                        styles.wifiWithFee,
                    )}
                  ></i>

                  {wifiAmenity ? wifiAmenity.displayText : translations.wifi_unknown}
                </div>

                <div className={styles.flightAmenityDescription}>
                  <i
                    className={clsx(
                      styles.sprite,
                      powerAmenity === undefined && styles.powerUnknown,
                      !!powerAmenity && !powerAmenity.exists && styles.powerNotAvailable,
                      !!powerAmenity &&
                        powerAmenity.exists &&
                        powerAmenity.free &&
                        styles.powerFree,
                      !!powerAmenity &&
                        powerAmenity.exists &&
                        !powerAmenity.free &&
                        styles.powerWithFee,
                    )}
                  ></i>

                  {powerAmenity ? powerAmenity.displayText : translations.power_unknown}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div data-pw='segment_arrivalInfo' className={styles.end}>
        <div className={styles.dateTime}>
          <div
            className={clsx(
              styles.date,
              segment.arrivalDate !== leg.segments[0].departureDate && styles.notSameDay,
            )}
          >
            {segment.arrivalDate}
          </div>

          <div className={styles.time} data-testid='arrive-time'>
            {segment.arrivalTime}
          </div>
        </div>

        <div
          className={clsx(styles.point, segmentIndex === leg.segments.length - 1 && styles.last)}
        ></div>

        <div className={styles.airport}>
          <span className={styles.airportCode}>{segment.arrivalAirportCode}</span>

          {segment.arrivalAirportName}
          {segment.arrivalTerminal ? ` - ${translations.terminal} ${segment.arrivalTerminal}` : ''}
        </div>
      </div>

      {segment?.stopoverDuration ? (
        <div
          data-pw='segment_layoverInfo'
          className={clsx(
            styles.stopover,
            !!stopoverIndicator && stopoverIndicator !== 'changeFlight' && styles.withDescription,
          )}
        >
          <div className={styles.verticalLine}></div>

          <div className={styles.stopoverInformation} data-testid='stopover'>
            {stopoverIndicator ? (
              <div>
                {translateText(
                  getStopoverIndicatorMessage(stopoverIndicator, translations),
                  locale,
                  segment.arrivalCityName!,
                )}
              </div>
            ) : null}

            <div>
              {translations.layover_time}{' '}
              <span
                data-testid='stopover-duration'
                className={clsx(
                  styles.stopoverDuration,
                  segment.stopoverDurationMinutes > 240 && styles.alertText,
                )}
              >
                {segment.stopoverDuration}
              </span>
            </div>

            {stopoverIndicator ? (
              <div className={styles.stopoverIndicatorDescription}>
                {getStopoverIndicatorMessageDescription(stopoverIndicator, translations)}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Segment;
